import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User, UserManagerSettings, WebStorageStateStore, Log } from 'oidc-client';
import { firstValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { LOCAL_STORAGE } from './storage';
import { Environment } from '@env/environment';
import { AuthRoutes } from '@app/core/components/auth/auth-routes';
import { LogService } from './log.service';
import { UserMetadata } from '../models/user-metadata-dto';
import { IMPERSONATION_STATE_KEY, KpaUserManager } from './kpa-user-manager';
import { IdentityManagementService } from './identity-management.service';
import { TokenPayload } from '../models/token-payload-dto';
import { jwtDecode } from 'jwt-decode';
import { EmployeeService } from '@app/employee/employee/shared/services/employee.service';
import { HrmPermissions, MkoPermissions, UserInfoService } from './user-info.service';
import { ApplicationFeature } from '../models/application-feature-enums';
import { RegulatoryDataFilterService } from '@app/environment/shared/services/regulatory-data-filter.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	// Constants
	private static readonly _loginAsAcrKey = 'loginas';

	private _user: ReplaySubject<User>;

	public get user(): Observable<User> {
		return this._user;
	}

	private _originator: ReplaySubject<User>;

	public get originator(): Observable<User> {
		return this._originator;
	}

	private _isLoggedIn: Observable<boolean>;

	public get isLoggedIn(): Observable<boolean> {
		return this._isLoggedIn;
	}

	private _sessionId: Observable<string>;

	public get sessionId(): Observable<string> {
		return this._sessionId;
	}

	private _accessToken: Observable<string>;

	public get accessToken(): Observable<string> {
		return this._accessToken;
	}

	private _originatorAccessToken: Observable<string>;

	public get originatorAccessToken(): Observable<string> {
		return this._originatorAccessToken;
	}

	private _authorizationHeader: Observable<string>;

	public get authorizationHeader(): Observable<string> {
		return this._authorizationHeader;
	}

	private _originatorAuthorizationHeader: Observable<string>;

	public get authorizationOriginatorHeader(): Observable<string> {
		return this._originatorAuthorizationHeader;
	}

	private _isImpersonating: Observable<boolean>;

	public get isImpersonating(): Observable<boolean> {
		return this._isImpersonating;
	}

	private readonly manager: KpaUserManager;

	private get clientSettings(): UserManagerSettings {
		return {
			authority: this.environment.services['sso'],
			client_id: this.environment.auth0.clientId,
			redirect_uri: location.origin + this.router.createUrlTree(['/' + AuthRoutes.AuthenticateCallback]).toString(),
			response_type: 'id_token token',
			scope: 'openid roles profile email kpa_platform_api',
			filterProtocolClaims: true,
			loadUserInfo: true,
			userStore: new WebStorageStateStore({ store: this.storage }),
		};
	}

	constructor(
		private readonly router: Router,
		private readonly environment: Environment,
		private readonly logger: LogService,
		private readonly identityManagementService: IdentityManagementService,
		private readonly employeeService: EmployeeService,
		// localStorage (not sessionStorage) so a user already signed in doesn't get a
		// fresh Auth0 round trip (and a redundant MKO/HRD bootstrap chain) just from
		// opening a new tab. Impersonation state lives in the token/Auth0 itself now,
		// not in browser storage, so sharing this across tabs doesn't change who a new
		// tab is acting as.
		@Inject(LOCAL_STORAGE) private readonly storage: Storage,
		@Inject(LOCAL_STORAGE) private readonly impersonationStorage: Storage,
		private readonly regulatoryDataFilterService: RegulatoryDataFilterService
	) {
		Log.logger = this.logger;
		this.manager = new KpaUserManager(this.clientSettings, impersonationStorage);
		this.createObservables();
	}

	private createObservables(): void {
		this._user = new ReplaySubject<User>(1);

		this._isLoggedIn = this._user.pipe(map((u) => !!u && !u.expired));

		this._sessionId = this._user.pipe(map((u) => (u && u.profile && u.profile.sid) || null));

		const accessToken$ = this._user.pipe(
			map((u) => (u && u.access_token) || null),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this._accessToken = accessToken$;
		this._originatorAccessToken = accessToken$;

		const authHeader$ = accessToken$.pipe(
			map((t) => (t ? `Bearer ${t}` : null)),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this._authorizationHeader = authHeader$;
		this._originatorAuthorizationHeader = authHeader$;

		this._originator = new ReplaySubject<User>(1);
		this._isImpersonating = this._user.pipe(
			map((u) => {
				const { UserUniversalId, ImpersonateeUserId } = this.manager.getMetadataFromUser(u);
				return !!ImpersonateeUserId && ImpersonateeUserId !== UserUniversalId;
			}),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		// seed
		this.manager.getUser().then((u) => this._user.next(u));
		this.manager.getOriginator().then((o) => this._originator.next(o));
	}

	public startAuthenticationAsync(returnUrl: string = '/landing'): Promise<void> {
		return this.manager.signinRedirect({ state: { returnUrl: returnUrl } });
	}

	/**
	 * Wrapper for UserManager.completeAuthenticationCallback to support Impersonation   *
	 * @param allowImpersonate True for ImpersonateCallback, otherwise false.
	 * @returns The redirect URL requested for the user.
	 */
	public async completeAuthenticationAsync(allowImpersonate: boolean = false): Promise<string> {
		const authenticatedUser = await this.manager.completeAuthenticationCallback(allowImpersonate);
		this._user.next(authenticatedUser);

		const { UserUniversalId, ImpersonateeUserId } = this.manager.getMetadataFromUser(authenticatedUser);
		const isImpersonating = ImpersonateeUserId !== UserUniversalId;
		if (isImpersonating) {
			this._originator.next(authenticatedUser);
		}
		// Return redirect as necessary
		const authenticatedUserState = authenticatedUser.state as any;
		return authenticatedUserState?.returnUrl || '';
	}

	/**
	 * Log out of the application and clear all storage
	 * @param fromExternal True if a federated logout and redirect to OIDC is not needed; otherwise false.
	 */
	public async logoutAsync(hasMko: boolean, hasHrDrive: boolean, fromExternal: boolean = false): Promise<void> {
		// Check the Originator object to end Impersonation or log out.
		// If there is an Originator, stop Impersonation.  Otherwise, log the
		// (not Impersonating) User out of the application.
		const originator = await this.manager.getOriginator();

		if (originator) {
			await this.endImpersonationAsync(hasMko, hasHrDrive);
		} else {
			let backToAngular: string = '';

			if (hasHrDrive && !hasMko) {
				if (fromExternal == true) {
					backToAngular = `${window.location.origin}/${AuthRoutes.LogoutExternalCommit}`;
				} else {
					backToAngular = `${window.location.origin}/${AuthRoutes.LogoutCommit}`;
				}
				this.redirectToServerClearHRM(backToAngular);
			} else {
				if (fromExternal == true) {
					backToAngular = `${window.location.origin}/${AuthRoutes.ClearSessionExternalHRM}`;
				} else {
					backToAngular = `${window.location.origin}/${AuthRoutes.ClearSessionHRM}`;
				}
				this.redirectToServerClearMKO(backToAngular);
			}
		}
	}

	/**
	 * Log out of the application and clear all storage
	 * @param fromExternal True if a federated logout and redirect to OIDC is not needed; otherwise false.
	 */
	public async commitLogoutAsync(fromExternal: boolean = false): Promise<void> {
		// Clear client-side auth state before any redirect/unload starts so the browser
		// cannot restore a still-authenticated SPA state via back/forward navigation.
		await this.manager.storeUser(null);
		this.storage.clear();
		this.impersonationStorage.clear();
		this.regulatoryDataFilterService.clear();
		this._originator.next(null);
		this._user.next(null);

		if (!fromExternal) {
			await this.manager.signoutRedirect();
		}
	}

	/**
	 * Begin an impersonated session.
	 * Originator must have SecurityPermission.Impersonate permission.
	 * @param userId  UserID of the user to be impersonated.
	 * @param returnUrl Redirect Url following successful impersonation authentication.
	 * @param callbackUrl - URL used after the authentication has occurred.
	 */
	public async startImpersonationAsync(
		userId: string,
		returnUrl: string = '/landing',
		callbackUrl: string = AuthRoutes.ImpersonateCallback // e.g. 'auth/impersonate-callback'
	): Promise<void> {
		// Build state for the round-trip/callback
		// Set up state for the signin
		const state = { returnUrl: returnUrl };
		state[IMPERSONATION_STATE_KEY] = true;

		// Must be signed in (the "actor")
		const actor = await this.manager.getUser();
		if (!actor?.access_token) throw new Error('Not signed in; cannot impersonate.');

		// Prefer your app metadata ID; fall back to sub if needed
		const meta = this.manager.getMetadataFromUser(actor);
		const userUniversalId = meta?.UserUniversalId ?? (actor.profile?.sub as string | undefined);
		if (!userUniversalId) throw new Error('Actor identifier missing.');

		// 1) Call your API; await a boolean before proceeding
		const ok = await firstValueFrom(
			this.identityManagementService
				.geImpersonationStart({
					UserUniversalId: userUniversalId,
					ImpersonateeUserId: userId,
				})
				.pipe(
					take(1),
					catchError(() => of(false))
				)
		);

		if (!ok) {
			const impersonatedUserName = sessionStorage.getItem('pending-impersonation-name') ?? 'this person';
			sessionStorage.setItem('impersonation-error', impersonatedUserName);
			sessionStorage.removeItem('pending-impersonation-name');
			window.location.assign(returnUrl);
			return;
		} else {
			sessionStorage.removeItem('pending-impersonation-name');
		}

		// Begin the signin
		return this.manager.signinRedirect({
			prompt: 'none',
			acr_values: `${AuthService._loginAsAcrKey}:${userId}`,
			redirect_uri: location.origin + this.router.createUrlTree([`/${callbackUrl}`]).toString(),
			state: state,
		});
	}

	public getMetadataFromUserAsync(user: User): UserMetadata {
		return this.manager.getMetadataFromUser(user);
	}

	/**
	 * Cease an impersonated session and return control to the Originator session.
	 */
	public async endImpersonationAsync(hasMko: boolean, hasHrDrive: boolean): Promise<void> {
		// Get rid of the Impersonated User and retrieve the Originator
		// from Local Storage

		// 1) Call your API; await a boolean before proceeding
		const ok = await firstValueFrom(this.identityManagementService.geImpersonationEnd().pipe(take(1)));
		if (!ok) throw new Error('Impersonation start failed.');

		// 2) Redirect to ASP.NET page to clear cookies/session, then come back
		let backToAngular = null;
		if (hasHrDrive && !hasMko) {
			backToAngular = `${window.location.origin}/${AuthRoutes.EndImpersonationCommit}`;
			this.redirectToServerClearHRM(backToAngular);
		} else {
			backToAngular = `${window.location.origin}/${AuthRoutes.EndImpersonationHRM}`;
			this.redirectToServerClearMKO(backToAngular);
		}
		return;
	}

	redirectToServerClearMKO(returnUrl: string) {
		const clearUrl = this.buildServerClearMKOUrl(returnUrl);

		// Full navigation so the browser accepts Set-Cookie expirations
		window.location.assign(clearUrl);
	}

	redirectToServerClearHRM(returnUrl: string) {
		const clearUrl = this.buildServerClearHRMUrl(returnUrl);

		// Full navigation so the browser accepts Set-Cookie expirations
		window.location.assign(clearUrl);
	}

	private buildServerClearMKOUrl(returnUrl: string): string {
		return `${this.environment.baseDomain['myKpaOnline']}/ClearState.aspx` + `?returnUrl=${encodeURIComponent(returnUrl)}`;
	}

	private buildServerClearHRMUrl(returnUrl: string): string {
		return `${this.environment.baseDomain['hrDrive']}/Account/ClearState` + `?returnUrl=${encodeURIComponent(returnUrl)}`;
	}

	public buildImpersonationRedirectUri(finalRedirectUri: string): string {
		return this.buildServerClearMKOUrl(this.buildServerClearHRMUrl(finalRedirectUri));
	}

	async commitEndImpersonation(returnUrl?: string) {
		// Stop here because we're navigating away

		const user = await this.manager.endImpersonationCallback();
		// Clean up the state of the User Observables
		this.regulatoryDataFilterService.clear();
		this._originator.next(null);
		this._user.next(user);

		// Force a new Impersonation call as the Originator so that
		// the Session can be replaced
		// Begin the signin
		return this.manager.signinRedirect({ state: { returnUrl: returnUrl } });
	}
}
